import React from 'react';
import { useSelector } from 'react-redux';

import Link from '@mlb/react-patterns/v4/components/Link/link';

import buildAudioUrl from '@gameday/utils/buildAudioUrl';
import useTracking from '@gameday/utils/hooks/useTracking';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import { getMediaLinksState } from '../store/selectors/getMediaLinks';

import { AudioWrapper, NoFeedWrapper } from '../medialinks.style';

import AudioFeedModel from './AudioFeedModel';

const MILB_GAME_CONTEXT = 'milb';
const FORCED_AUDIO_LINK_CONTEXTS = [{
    context: MILB_GAME_CONTEXT,
    textKey: 'button_milb_audio',
    textDefault: 'MiLB Audio'
}];

const feedModelHelper = (feed, teams, gamePk) => new AudioFeedModel(feed, teams, gamePk);

const getAudioList = ({ audio, gamePk, teams, translate, gameContext }) => {
    // First look through the evergreen list to see if we should return early
    for (let i = 0; i < FORCED_AUDIO_LINK_CONTEXTS.length; i++) {
        const { context, textKey, textDefault } = FORCED_AUDIO_LINK_CONTEXTS[i] || {};
        if (context === gameContext) {
            return {
                all: [{
                    gamePk,
                    type: 'audio',
                    buttonText: translate(textKey, textDefault)
                }]
            };
        }
    }

    const mediaOffText = translate('gameday_media_off', 'No active feeds available');
    const feeds = {};
    const list = audio?.list || [];
    const audioTeams = audio?.teams;
    const hasAudio = audio?.hasAudio;
    const audioTeamsHome = audioTeams?.home || [];
    const audioTeamsAway = audioTeams?.away || [];
    const displayMediaStatus = list?.[0]?.mediaState;

    if (displayMediaStatus === 'MEDIA_OFF') {
        feeds.directive = mediaOffText;
    } else {
        feeds.directive = (hasAudio) ? '' : mediaOffText;
        feeds.home = audioTeamsHome.map((team) => {
            return feedModelHelper(team, teams, gamePk);
        });
        feeds.away = audioTeamsAway.map((team) => {
            return feedModelHelper(team, teams, gamePk);
        });
        feeds.all = (feeds.home || []).concat(feeds.away || []);
    }

    return feeds;
};

const AudioList = () => {
    const {
        audio,
        gamePk,
        gameContext,
        teams
    } = useSelector(getMediaLinksState);

    const translate = useTranslate();
    const { all = [], directive = '' } = getAudioList({ audio, gamePk, teams, translate, gameContext }) || {};
    let content = <></>;
    const Tracking = useTracking();

    const handlers = {
        onClick: (e) => {
            const text = e?.currentTarget.innerText || '';

            const cfg = {
                componentName: ['Listen', text],
                selectedIndex: e?.currentTarget?.dataset.index,
                text,
                activity: 'Link Click'
            };

            Tracking.track.track_click(cfg);
        }
    };

    if (directive) {
        content = <NoFeedWrapper>{directive}</NoFeedWrapper>;
    } else if (all?.length > 0) {
        content = all?.map((item, index) => {
            const { displayCode, displayLanguage, callSign, gamePk, buttonText = '' } = item;
            const text = buttonText || displayCode + (displayLanguage === 'es' ? '  ES' : '') + ': ' + callSign;
            const url = buildAudioUrl({ gamePk, callSign }, gameContext);
            const dataAttr = { 'data-index': index };
            return (
                <li key={'audio-list' + index}>
                    <Link
                        dataAttr={dataAttr}
                        handlers={handlers}
                        ariaLabel={text}
                        target='_blank'
                        url={url}
                    >{text}
                    </Link>
                </li>
            );
        });
    }

    return (
        <AudioWrapper>
            <div>{translate('label_listen', 'Listen')}</div>
            <ul>{content}</ul>
        </AudioWrapper>
    );
};

export default AudioList;
